/**
 * Step Registry — frontend mirror of backend/app/services/step_registry.py.
 *
 * Defines the 8-step SP workflow taxonomy for rendering StepStrip,
 * mapping Pilot events to CFA fields, and checking prerequisites.
 */

export type StepScope = 'sub_project' | 'solution';
export type StepStatus = 'not_started' | 'in_progress' | 'ready' | 'locked';

export interface StepDefinition {
  stepId: string;
  label: string; // i18n key: pilot.steps.{stepId}
  scope: StepScope;
  cfaAgent: string;
  primaryFields: string[];
  prereqs: string[];
}

export const STEPS: StepDefinition[] = [
  {
    stepId: 'S0',
    label: 'Problem Statement',
    scope: 'sub_project',
    cfaAgent: 'problem_statement',
    primaryFields: ['problem_statement'],
    prereqs: [],
  },
  {
    stepId: 'S1',
    label: 'State of the Art',
    scope: 'sub_project',
    cfaAgent: 'sota',
    primaryFields: ['state_of_the_art'],
    prereqs: [],
  },
  {
    stepId: 'S2',
    label: 'Solution Description',
    scope: 'solution',
    cfaAgent: 'solution_desc',
    primaryFields: ['solution_description'],
    prereqs: ['S1'],
  },
  {
    stepId: 'S3',
    label: 'Freedom to Operate',
    scope: 'solution',
    cfaAgent: 'fto',
    primaryFields: ['fto_analysis'],
    prereqs: ['S2'],
  },
  {
    stepId: 'S4',
    label: 'Industrialization',
    scope: 'solution',
    cfaAgent: 'industrialization',
    primaryFields: ['industrialization'],
    prereqs: ['S2'],
  },
  {
    stepId: 'S5',
    label: 'Patentability',
    scope: 'solution',
    cfaAgent: 'patentability',
    primaryFields: ['patentability'],
    prereqs: ['S2'],
  },
  {
    stepId: 'S6',
    label: 'Claims',
    scope: 'solution',
    cfaAgent: 'claims',
    primaryFields: ['claims'],
    prereqs: ['S5'],
  },
  {
    stepId: 'S7',
    label: 'Patent Application',
    scope: 'solution',
    cfaAgent: 'patent_application',
    primaryFields: ['patent_application'],
    prereqs: ['S6'],
  },
];

export const STEP_BY_ID: Record<string, StepDefinition> = Object.fromEntries(
  STEPS.map(s => [s.stepId, s])
);

/** Get the primary activeField for a step (first primary field). */
export function getActiveFieldForStep(stepId: string): string | null {
  const step = STEP_BY_ID[stepId];
  return step?.primaryFields[0] ?? null;
}
